import React, { useState, useEffect } from 'react';
import { ArrowDown, Settings, ChevronDown, Repeat, Wallet, AlertCircle, CheckCircle, X, RefreshCw, ExternalLink, Globe, Droplets, Sprout, Layers } from 'lucide-react';

const EntropySwap = () => {
  const BACKEND_API = window.location.origin;
  
  // Network configuration for Entropychain
  const networks = {
    ENTROPY: {
      id: 'ENTROPY',
      name: 'Entropychain',
      chainId: '0x4DC7',
      chainIdDecimal: 19911,
      logo: '🔗', // Placeholder - update when new logo is ready
      rpc: 'https://rpc-mainnet.entropychain.live',
      explorer: 'https://scan.entropychain.live',
      nativeCurrency: { name: 'KGF.e', symbol: 'KGF.e', decimals: 18 }
    }
  };

  // Available tokens on Entropychain
  const availableTokens = [
    { 
      symbol: 'KGF.e', 
      name: 'Gold-backed KGF', 
      logo: '🪙',
      isEmoji: true,
      isNative: true, 
      address: '0x0',
      description: ' '
    },
{ 
      symbol: 'KGF', 
      name: 'Gold-backed KGF', 
      logo: '🪙',
      isEmoji: true,
      isNative: false, 
      address: '0x0',
      description: ' '
    },
{ 
      symbol: 'TMC', 
      name: 'Track My Chain', 
      logo: '🪙',
      isEmoji: true,
      isNative: false, 
      address: '0x0',
      description: ' '
    },
{ 
      symbol: 'ACC', 
      name: 'Authentic Chain', 
      logo: '🪙',
      isEmoji: true,
      isNative: false, 
      address: '0x0',
      description: ' '
    },
    { 
      symbol: 'USDT', 
      name: 'Tether USD (BEP20)', 
      logo: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
      isEmoji: false,
      address: '0x55d398326f99059fF775485246999027B3197955' 
    },
    { 
      symbol: 'BTC', 
      name: 'Bitcoin (BEP20)', 
      logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png',
      isEmoji: false,
      address: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c' 
    },
    { 
      symbol: 'BNB', 
      name: 'Binance Coin', 
      logo: 'https://cryptologos.cc/logos/bnb-bnb-logo.png',
      isEmoji: false,
      address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c' 
    }
  ];

  const [currentTab, setCurrentTab] = useState('swap');
  const [fromToken, setFromToken] = useState(availableTokens[0]);
  const [toToken, setToToken] = useState(availableTokens[1]);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [nativeBalance, setNativeBalance] = useState('0.00');
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [selectingFor, setSelectingFor] = useState('from');
  const [slippage, setSlippage] = useState('0.5');
  const [showSettings, setShowSettings] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [txStatus, setTxStatus] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [goldPrice, setGoldPrice] = useState(0);
  const [livePrices, setLivePrices] = useState({
    'KGF.e': 0,
    USDT: 1,
    BTC: 45000,
    BNB: 300
  });

  // Fetch live gold price (1mg of gold)
  useEffect(() => {
    const fetchGoldPrice = async () => {
      try {
        const response = await fetch(
          'https://api.metals.live/v1/spot/gold'
        );
        const data = await response.json();
        // Gold price per troy ounce, convert to per mg
        // 1 troy ounce = 31,103.5 mg
        const pricePerMg = data[0]?.price / 31103.5 || 0.065;
        setGoldPrice(pricePerMg);
        setLivePrices(prev => ({
          ...prev,
          'KGF.e': pricePerMg
        }));
      } catch (error) {
        console.error('Gold price fetch error:', error);
        // Fallback to approximate price
        setGoldPrice(0.065);
        setLivePrices(prev => ({
          ...prev,
          'KGF.e': 0.065
        }));
      }
    };

    const fetchCryptoPrices = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,binancecoin,tether&vs_currencies=usd'
        );
        const data = await response.json();
        
        setLivePrices(prev => ({
          ...prev,
          BTC: data.bitcoin?.usd || prev.BTC,
          BNB: data.binancecoin?.usd || prev.BNB,
          USDT: data.tether?.usd || 1
        }));
      } catch (error) {
        console.error('Crypto price fetch error:', error);
      }
    };

    fetchGoldPrice();
    fetchCryptoPrices();
    const interval = setInterval(() => {
      fetchGoldPrice();
      fetchCryptoPrices();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Calculate exchange amount
  useEffect(() => {
    if (fromAmount && !isNaN(fromAmount)) {
      const amount = parseFloat(fromAmount);
      const fromPrice = livePrices[fromToken.symbol] || 1;
      const toPrice = livePrices[toToken.symbol] || 1;
      
      const outputAmount = (amount * fromPrice / toPrice).toFixed(8);
      setToAmount(outputAmount);
    } else {
      setToAmount('');
    }
  }, [fromAmount, fromToken, toToken, livePrices]);

  // Fetch balance
  const fetchBalance = async (address) => {
    try {
      if (typeof window.ethereum !== 'undefined' && address) {
        const balance = await window.ethereum.request({
          method: 'eth_getBalance',
          params: [address, 'latest']
        });
        
        const ethBalance = parseInt(balance, 16) / 1e18;
        setNativeBalance(ethBalance.toFixed(6));
      }
    } catch (error) {
      console.error('Balance fetch error:', error);
    }
  };

  // Connect wallet
  const connectWallet = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        const accounts = await window.ethereum.request({ 
          method: 'eth_requestAccounts' 
        });
        
        setWalletAddress(accounts[0]);
        setIsWalletConnected(true);
        await switchToEntropychain();
        await fetchBalance(accounts[0]);
        
        window.ethereum.on('accountsChanged', (accounts) => {
          if (accounts.length > 0) {
            setWalletAddress(accounts[0]);
            fetchBalance(accounts[0]);
          } else {
            disconnectWallet();
          }
        });
        
        window.ethereum.on('chainChanged', () => {
          window.location.reload();
        });
      } else {
        alert('Please install MetaMask or use a Web3 wallet');
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
    }
  };

  const disconnectWallet = () => {
    setIsWalletConnected(false);
    setWalletAddress('');
    setNativeBalance('0.00');
  };

  const switchToEntropychain = async () => {
    const network = networks.ENTROPY;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: network.chainId }],
      });
    } catch (error) {
      if (error.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: network.chainId,
              chainName: network.name,
              nativeCurrency: network.nativeCurrency,
              rpcUrls: [network.rpc],
              blockExplorerUrls: [network.explorer]
            }]
          });
        } catch (addError) {
          console.error('Add network error:', addError);
        }
      }
    }
  };

  const handleTokenSelect = (token) => {
    if (selectingFor === 'from') {
      if (token.symbol === toToken.symbol) {
        setToToken(fromToken);
      }
      setFromToken(token);
    } else {
      if (token.symbol === fromToken.symbol) {
        setFromToken(toToken);
      }
      setToToken(token);
    }
    setShowTokenModal(false);
  };

  const switchTokens = () => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    const tempAmount = fromAmount;
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handleSwap = async () => {
    if (!isWalletConnected) {
      await connectWallet();
      return;
    }
    
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    setIsSwapping(true);
    setTxStatus('pending');
    
    try {
      const amountInWei = (parseFloat(fromAmount) * 1e18).toString(16);
      
      const swapRequest = {
        fromToken: fromToken.symbol,
        toToken: toToken.symbol,
        fromAmount: fromAmount,
        toAmount: toAmount,
        userAddress: walletAddress,
        slippage: slippage
      };

      const response = await fetch(`${BACKEND_API}/api/v2/swap/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(swapRequest)
      });

      const swapData = await response.json();

      if (!swapData.success) {
        throw new Error(swapData.message || 'Swap initiation failed');
      }

      const txParams = {
        from: walletAddress,
        to: swapData.depositAddress,
        value: '0x' + amountInWei,
        gas: '0x5208',
      };
      
      const txHashResult = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });
      
      setTxHash(txHashResult);

      await fetch(`${BACKEND_API}/api/v2/swap/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          swapId: swapData.swapId,
          txHash: txHashResult,
          userAddress: walletAddress
        })
      });

      setTxStatus('success');
      setIsSwapping(false);
      
      setTimeout(() => {
        setTxStatus(null);
        setFromAmount('');
        setToAmount('');
        fetchBalance(walletAddress);
      }, 5000);
    } catch (error) {
      console.error('Swap error:', error);
      setTxStatus('error');
      setIsSwapping(false);
      setTimeout(() => setTxStatus(null), 3000);
      alert('Transaction failed: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-cyan-500 flex items-center justify-center text-2xl">
                🔷
              </div>
              <div>
                <div className="text-lg md:text-2xl font-bold text-gray-800">
                  ENTROPY SWAP
                </div>
                <div className="text-xs text-gray-500">Powered by Gold</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                <Globe className="w-4 h-4 text-gray-600" />
                <span className="text-xs font-semibold text-gray-700">Entropychain</span>
              </div>
              
              {isWalletConnected ? (
                <div className="relative group">
                  <button className="px-2 md:px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-semibold flex items-center gap-1 text-xs md:text-sm">
                    <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                    {walletAddress.slice(0, 4)}...{walletAddress.slice(-3)}
                  </button>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all border border-gray-200">
                    <div className="p-3 border-b border-gray-200">
                      <div className="text-xs text-gray-500">Balance</div>
                      <div className="font-bold text-sm text-gray-800">{nativeBalance} KGF.e</div>
                      <div className="text-xs text-gray-600">≈ ${(parseFloat(nativeBalance) * livePrices['KGF.e']).toFixed(2)} USD</div>
                    </div>
                    <button onClick={disconnectWallet} className="w-full px-4 py-2 text-left hover:bg-gray-50 rounded-b-2xl text-red-500 text-sm">
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  onClick={connectWallet}
                  className="px-3 md:px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-semibold flex items-center gap-1 hover:shadow-lg transition-all text-xs md:text-sm"
                >
                  <Wallet className="w-4 h-4" />
                  <span className="hidden sm:inline">Connect Wallet</span>
                </button>
              )}
            </div>
          </div>
          
          <nav className="flex gap-2 mt-3 overflow-x-auto pb-1">
            <button 
              onClick={() => setCurrentTab('swap')}
              className={`px-3 py-1.5 rounded-full font-semibold text-xs whitespace-nowrap transition-all ${
                currentTab === 'swap' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Repeat className="w-3 h-3 inline mr-1" />
              Swap
            </button>
            <button 
              onClick={() => setCurrentTab('liquidity')}
              className={`px-3 py-1.5 rounded-full font-semibold text-xs whitespace-nowrap transition-all ${
                currentTab === 'liquidity' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Droplets className="w-3 h-3 inline mr-1" />
              Liquidity
            </button>
            <button 
              onClick={() => setCurrentTab('farm')}
              className={`px-3 py-1.5 rounded-full font-semibold text-xs whitespace-nowrap transition-all ${
                currentTab === 'farm' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Sprout className="w-3 h-3 inline mr-1" />
              Farm
            </button>
            <button 
              onClick={() => setCurrentTab('pool')}
              className={`px-3 py-1.5 rounded-full font-semibold text-xs whitespace-nowrap transition-all ${
                currentTab === 'pool' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Layers className="w-3 h-3 inline mr-1" />
              Pool
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-3 py-6 md:py-12">
        <div className="max-w-lg mx-auto">
          {currentTab === 'swap' && (
            <div className="bg-white rounded-3xl p-4 md:p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2 text-gray-800">
                  <Repeat className="w-5 h-5 md:w-6 md:h-6 text-purple-500" />
                  Swap
                </h2>
                <button 
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Settings className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {showSettings && (
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <h3 className="font-semibold mb-3 text-sm md:text-base text-gray-800">Slippage Tolerance</h3>
                  <div className="flex gap-2">
                    {['0.1', '0.5', '1.0'].map((val) => (
                      <button
                        key={val}
                        onClick={() => setSlippage(val)}
                        className={`px-3 md:px-4 py-2 rounded-xl font-semibold transition-all text-sm ${
                          slippage === val
                            ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:border-purple-300'
                        }`}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-2">
                <div className="flex justify-between text-xs md:text-sm text-gray-600 mb-2">
                  <span>From</span>
                  <span>Balance: {isWalletConnected ? nativeBalance : '0.00'}</span>
                </div>
                <div className="bg-gray-50 rounded-2xl p-3 md:p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <input
                      type="number"
                      value={fromAmount}
                      onChange={(e) => setFromAmount(e.target.value)}
                      placeholder="0.0"
                      className="text-2xl md:text-3xl font-bold bg-transparent border-none outline-none w-full text-gray-800"
                    />
                    <button
                      onClick={() => {
                        setSelectingFor('from');
                        setShowTokenModal(true);
                      }}
                      className="flex items-center gap-1.5 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-white hover:bg-gray-100 rounded-2xl transition-colors flex-shrink-0 border border-gray-200"
                    >
                      {fromToken.isEmoji ? (
                        <span className="text-xl">{fromToken.logo}</span>
                      ) : (
                        <img src={fromToken.logo} alt={fromToken.symbol} className="w-5 h-5 md:w-6 md:h-6 rounded-full" onError={(e) => e.target.style.display = 'none'} />
                      )}
                      <span className="font-semibold text-sm md:text-base text-gray-800">{fromToken.symbol}</span>
                      <ChevronDown className="w-3 h-3 md:w-4 md:h-4 text-gray-600" />
                    </button>
                  </div>
                  <div className="text-xs md:text-sm text-gray-500">
                    ≈ ${((parseFloat(fromAmount || 0) * livePrices[fromToken.symbol])).toFixed(2)} USD
                  </div>
                </div>
              </div>

              <div className="flex justify-center -my-2 z-10 relative">
                <button
                  onClick={switchTokens}
                  className="p-3 bg-white hover:bg-gray-50 rounded-xl border-4 border-gray-50 transition-all hover:rotate-180 shadow-md"
                >
                  <ArrowDown className="w-5 h-5 text-purple-500" />
                </button>
              </div>

              <div className="mb-4 md:mb-6">
                <div className="flex justify-between text-xs md:text-sm text-gray-600 mb-2">
                  <span>To</span>
                  <span>Balance: 0.00</span>
                </div>
                <div className="bg-gray-50 rounded-2xl p-3 md:p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <input
                      type="number"
                      value={toAmount}
                      readOnly
                      placeholder="0.0"
                      className="text-2xl md:text-3xl font-bold bg-transparent border-none outline-none w-full text-gray-800"
                    />
                    <button
                      onClick={() => {
                        setSelectingFor('to');
                        setShowTokenModal(true);
                      }}
                      className="flex items-center gap-1.5 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-white hover:bg-gray-100 rounded-2xl transition-colors flex-shrink-0 border border-gray-200"
                    >
                      {toToken.isEmoji ? (
                        <span className="text-xl">{toToken.logo}</span>
                      ) : (
                        <img src={toToken.logo} alt={toToken.symbol} className="w-5 h-5 md:w-6 md:h-6 rounded-full" onError={(e) => e.target.style.display = 'none'} />
                      )}
                      <span className="font-semibold text-sm md:text-base text-gray-800">{toToken.symbol}</span>
                      <ChevronDown className="w-3 h-3 md:w-4 md:h-4 text-gray-600" />
                    </button>
                  </div>
                  <div className="text-xs md:text-sm text-gray-500">
                    ≈ ${((parseFloat(toAmount || 0) * livePrices[toToken.symbol])).toFixed(2)} USD
                  </div>
                </div>
              </div>

              {fromAmount && toAmount && (
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <div className="space-y-2 text-xs md:text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rate</span>
                      <span className="font-semibold text-gray-800">1 {fromToken.symbol} ≈ {(parseFloat(toAmount) / parseFloat(fromAmount)).toFixed(8)} {toToken.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Min Received</span>
                      <span className="font-semibold text-gray-800">{(parseFloat(toAmount) * (1 - parseFloat(slippage) / 100)).toFixed(8)} {toToken.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Network Fee</span>
                      <span className="font-semibold text-gray-800">~$0.01</span>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleSwap}
                disabled={!fromAmount || isSwapping}
                className={`w-full py-3 md:py-4 rounded-2xl font-bold text-base md:text-lg transition-all ${
                  !fromAmount
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:shadow-xl active:scale-95'
                }`}
              >
                {!isWalletConnected ? (
                  'Connect Wallet'
                ) : !fromAmount ? (
                  'Enter Amount'
                ) : isSwapping ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                    Swapping...
                  </span>
                ) : (
                  'Swap'
                )}
              </button>

              {txStatus && (
                <div className={`mt-4 p-3 md:p-4 rounded-2xl border ${
                  txStatus === 'pending' 
                    ? 'bg-yellow-50 border-yellow-200' 
                    : txStatus === 'success'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-3">
                    {txStatus === 'pending' ? (
                      <RefreshCw className="w-5 h-5 text-yellow-600 animate-spin" />
                    ) : txStatus === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-sm md:text-base text-gray-800">
                        {txStatus === 'pending' ? 'Transaction Pending' : txStatus === 'success' ? 'Swap Successful!' : 'Transaction Failed'}
                      </div>
                      <div className="text-xs md:text-sm text-gray-600">
                        {txStatus === 'pending' ? 'Confirming...' : txStatus === 'success' ? 'Tokens swapped successfully' : 'Please try again'}
                      </div>
                      {txHash && txStatus === 'success' && (
                        <a 
                          href={`${networks.ENTROPY.explorer}/tx/${txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 text-xs flex items-center gap-1 mt-1 hover:underline"
                        >
                          View Transaction <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentTab === 'liquidity' && (
            <div className="bg-white rounded-3xl p-4 md:p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2 text-gray-800">
                  <Droplets className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
                  Add Liquidity
                </h2>
              </div>

              <div className="text-center py-12">
                <div className="text-6xl mb-4">💧</div>
                <h3 className="text-2xl font-bold mb-3 text-gray-800">Provide Liquidity</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Earn fees by providing liquidity to KGF.e trading pairs. Supply tokens to liquidity pools and receive LP tokens.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-4 rounded-2xl border border-purple-200">
                    <div className="text-purple-600 font-semibold mb-2">KGF.e/USDT Pool</div>
                    <div className="text-2xl font-bold mb-1 text-gray-800">0.3% Fee</div>
                    <div className="text-sm text-gray-600">TVL: $0</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-2xl border border-cyan-200">
                    <div className="text-cyan-600 font-semibold mb-2">KGF.e/BTC Pool</div>
                    <div className="text-2xl font-bold mb-1 text-gray-800">0.3% Fee</div>
                    <div className="text-sm text-gray-600">TVL: $0</div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-2xl border border-orange-200">
                    <div className="text-orange-600 font-semibold mb-2">KGF.e/BNB Pool</div>
                    <div className="text-2xl font-bold mb-1 text-gray-800">0.3% Fee</div>
                    <div className="text-sm text-gray-600">TVL: $0</div>
                  </div>
                </div>

                <button
                  onClick={connectWallet}
                  disabled={isWalletConnected}
                  className="mt-6 px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-2xl font-bold hover:shadow-xl transition-all disabled:opacity-50"
                >
                  {isWalletConnected ? 'Wallet Connected' : 'Connect Wallet to Add Liquidity'}
                </button>
              </div>
            </div>
          )}

          {currentTab === 'farm' && (
            <div className="bg-white rounded-3xl p-4 md:p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2 text-gray-800">
                  <Sprout className="w-5 h-5 md:w-6 md:h-6 text-green-500" />
                  Farms
                </h2>
              </div>

              <div className="text-center py-16">
                <div className="text-7xl mb-4">🌾</div>
                <h3 className="text-3xl font-bold mb-3 text-gray-800">Coming Soon</h3>
                <p className="text-gray-600 text-lg">
                  Stake your LP tokens to earn rewards
                </p>
              </div>
            </div>
          )}

          {currentTab === 'pool' && (
            <div className="bg-white rounded-3xl p-4 md:p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2 text-gray-800">
                  <Layers className="w-5 h-5 md:w-6 md:h-6 text-indigo-500" />
                  Pools
                </h2>
              </div>

              <div className="text-center py-16">
                <div className="text-7xl mb-4">🏊</div>
                <h3 className="text-3xl font-bold mb-3 text-gray-800">Coming Soon</h3>
                <p className="text-gray-600 text-lg">
                  Participate in liquidity pools and earn trading fees
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 md:gap-4 mt-4 md:mt-6">
            <div className="bg-white backdrop-blur rounded-2xl p-3 md:p-4 border border-gray-200 shadow-md">
              <div className="text-xs md:text-sm text-gray-600 mb-1">24h Volume</div>
              <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">$0</div>
            </div>
            <div className="bg-white backdrop-blur rounded-2xl p-3 md:p-4 border border-gray-200 shadow-md">
              <div className="text-xs md:text-sm text-gray-600 mb-1">Gold Price (1mg)</div>
              <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">${livePrices['KGF.e'].toFixed(4)}</div>
            </div>
          </div>

          <div className="mt-4 md:mt-6 bg-gradient-to-r from-pink-50 to-purple-50 border border-purple-200 rounded-2xl p-3 md:p-4">
            <div className="flex items-start gap-2 md:gap-3">
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-purple-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs md:text-sm text-gray-700">
                <strong className="text-gray-900">About KGF.e:</strong> Each KGF.e token represents 1 milligram of gold, providing a stable, asset-backed currency on Entropychain. Trade with confidence knowing your value is backed by precious metals.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Token Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="p-4 md:p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-800">Select Token</h3>
              <button onClick={() => setShowTokenModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-3 md:p-4 space-y-2 max-h-96 overflow-y-auto">
              {availableTokens.map((token) => (
                <button
                  key={token.symbol}
                  onClick={() => handleTokenSelect(token)}
                  className="w-full flex items-center gap-3 p-3 md:p-4 hover:bg-gray-50 rounded-2xl transition-colors border border-transparent hover:border-purple-200"
                >
                  {token.isEmoji ? (
                    <span className="text-3xl">{token.logo}</span>
                  ) : (
                    <img src={token.logo} alt={token.symbol} className="w-8 h-8 md:w-10 md:h-10 rounded-full" onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }} />
                  )}
                  <div className="text-left flex-1">
                    <div className="font-semibold text-sm md:text-base text-gray-800">{token.symbol}</div>
                    <div className="text-xs text-gray-600">{token.name}</div>
                    {token.description && (
                      <div className="text-xs text-purple-600 mt-1">{token.description}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-800">${livePrices[token.symbol]?.toFixed(token.symbol === 'KGF.e' ? 4 : 2) || '0.00'}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-12">
        <div className="container mx-auto px-4 py-6">
          <div className="text-center">
            <div className="text-gray-800 font-semibold mb-2">Entropy Swap</div>
            <div className="text-sm text-gray-600 mb-2">
              Decentralized exchange powered by gold-backed KGF.e tokens
            </div>
            <div className="text-xs text-gray-500">
              Chain ID: 19911 | RPC: rpc-mainnet.entropychain.live
            </div>
            <div className="text-xs text-purple-600 mt-2 font-semibold">
              entropyswap.live
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EntropySwap;